'use client';

import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Button, { ButtonVariant } from '../Button';
import buildImageSrc from '../../functions/image-functions-aspect-ratio';
import { classNames } from '../../utils/classNames';
import { useId } from 'react';
import { MediaType } from '../../types';
import ProductBanner from './ProductBanner';
import { RibbonProduct } from './productBannerHelpers';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import ProductPrice from '@repo/ui/src/components/Product/Price';

type BackgroundStyle = 'Light' | 'Dark';

export type ProductCardProduct = RibbonProduct & {
  title?: string;
  shortTitle?: string;
  mainImageUrl?: string;
  images?: Array<MediaType>;
  description?: string;
  category?: string;
  variations?: Array<{ attributesMap?: { grill_size?: string }; grillSize?: string }>;
  variationValues?: Array<{ name: string; value: string }>;
  price?: number;
  frontFacingImages?: {
    closedImage?: MediaType;
  };
  transparentImages?: Array<MediaType>;
  link?: string;
  deliveryKey?: string;
  productUrl?: string;
  originalPrice?: number;
  ratingSummary?: {
    average?: number;
    count?: number;
  };
};

export interface ProductCardProps {
  product: ProductCardProduct;
  backgroundStyle?: BackgroundStyle;
  vip?: boolean;
  newText?: string;
  saleText?: string;
  outOfStockText?: string;
  itemsPerRow?: number;
  className?: string;
}

const ProductCard = ({
  product,
  backgroundStyle,
  vip,
  newText,
  saleText,
  outOfStockText,
  itemsPerRow,
  className,
}: ProductCardProps) => {
  const { push } = useModal(ModalID.productDetails);
  const { title, shortTitle, mainImageUrl, images, category, link, deliveryKey, productUrl } =
    product;
  const href = link || deliveryKey || productUrl || '#';
  const isDark = backgroundStyle === 'Dark';
  const { t } = useTranslation();

  const imageUrl = mainImageUrl ?? buildImageSrc(images?.[0] ?? null, 300, 1, 1);
  const productTitle = shortTitle || title || '';
  const titleId = `product-card-title-${useId()}`;

  return (
    <article
      aria-labelledby={titleId}
      className={classNames(
        'relative h-full flex flex-col group border-r border-b border-neutral-200',
        className,
      )}
    >
      <div className="absolute w-full">
        <ProductBanner
          product={product}
          className="w-fit mx-auto"
          outOfStockText={outOfStockText}
          vip={vip}
          newText={newText}
          saleText={saleText}
        />
      </div>
      <Button
        link={href}
        as={Anchor}
        variant={ButtonVariant.bare}
        className={classNames(
          'w-full flex-grow flex flex-col justify-between pt-8 sm:pt-10 p-4 z-[100]',
          itemsPerRow === 5 ? 'sm:p-6' : 'sm:p-8 md:p-10',
        )}
        size="none"
        ariaLabelledBy={titleId}
      >
        <div className="relative w-full">
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={productTitle}
              layout="responsive"
              width={700}
              height={700}
              objectFit="contain"
              className="scale-90 sm:group-hover:scale-100 transition-transform duration-300"
              data-testid="product-card-image"
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          {category && (
            <p
              className={classNames(
                'tra-text-web-sub-2',
                isDark ? 'text-neutral-200' : 'text-neutral-500',
              )}
            >
              {t(`productCategories.${category.toLowerCase().replace(/ /g, '_')}`, undefined, {
                default: category,
              })}
            </p>
          )}
          <h3 id={titleId} data-testid="pc-title" className="tra-text-web-heading-6 mb-0">
            {(() => {
              const splitCategory = category?.toLowerCase();
              const shouldSplit =
                splitCategory === 'pellet grill' ||
                splitCategory === 'flat top grill' ||
                splitCategory === 'griddle';
              if (shouldSplit) {
                const [first, ...rest] = productTitle.split(' ');
                return rest.length > 0 ? (
                  <>
                    {first} <span className="text-brand-300">{rest.join(' ')}</span>
                  </>
                ) : (
                  first
                );
              }
              return productTitle;
            })()}
          </h3>
          <ProductPrice
            product={product}
            variant="bodyL"
            color={isDark ? 'white' : undefined}
            className="mb-0"
            vip={vip}
          />
        </div>
      </Button>
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <Button
          variant={ButtonVariant.secondary}
          size="small"
          onClick={() => push(product)}
          className="pointer-events-auto opacity-0 translate-y-16 sm:group-hover:opacity-100 sm:group-hover:translate-y-4 sm:group-focus-within:opacity-100 sm:group-focus-within:translate-y-4 transition-all duration-300 !w-[80%] text-center z-101"
        >
          {t('productCardContent.quickViewButtonLabel', undefined, { default: 'View Details' })}
        </Button>
      </div>
    </article>
  );
};

export default ProductCard;
